import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAccount } from '../contexts/AccountContext'
import { useCredits } from '../contexts/CreditsContext'
import { StudentProfile, WritingPrompt, WritingSubmission } from '../types/models'
import { PromptSelector } from './PromptSelector'
import { WritingInterface } from './WritingInterface'
import { AssessmentReport } from './AssessmentReport'
import { ProgressDashboard } from './ProgressDashboard'
import { ExampleWritings } from './ExampleWritings'
import { CreditBalance } from './CreditBalance'
import '../styles/Dashboard.css'

type DashboardView = 'home' | 'create-profile' | 'edit-profile' | 'select-prompt' | 'writing' | 'report' | 'progress' | 'examples'

interface ProfileStats {
  totalAssessments: number
  averageScore: number | null
}

export const Dashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const { studentProfiles, getProfiles, createProfile, deleteProfile, updateProfile, loading } = useAccount()
  const { credits, fetchCredits } = useCredits()
  const [view, setView] = useState<DashboardView>('home')
  const [editingProfile, setEditingProfile] = useState<StudentProfile | null>(null)
  const [selectedProfile, setSelectedProfile] = useState<StudentProfile | null>(null)
  const [selectedPrompt, setSelectedPrompt] = useState<WritingPrompt | null>(null)
  const [lastSubmission, setLastSubmission] = useState<WritingSubmission | null>(null)
  const [examplesYearLevel, setExamplesYearLevel] = useState<number>(5)
  const [profileStats, setProfileStats] = useState<Record<string, ProfileStats>>({})
  const initialFetchDone = useRef(false)

  // Form state
  const [formName, setFormName] = useState('')
  const [formYearLevel, setFormYearLevel] = useState<number>(5)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null)

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true
      getProfiles()
      fetchCredits()
    }
  }, [])

  // Fetch stats for all profiles when profiles change
  useEffect(() => {
    const fetchProfileStats = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
      const storedTokens = localStorage.getItem('authTokens')
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null
      
      if (!token) return
      
      const newStats: Record<string, ProfileStats> = {}
      
      for (const profile of studentProfiles) {
        try {
          const response = await fetch(`${apiUrl}/api/progress/${profile.id}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.ok) {
            const data = await response.json()
            const avgScore = data.statistics?.averageScore
            newStats[profile.id] = {
              totalAssessments: data.totalAssessments || 0,
              averageScore: avgScore != null ? Number(avgScore) : null
            }
          } else {
            newStats[profile.id] = { totalAssessments: 0, averageScore: null }
          }
        } catch (err) {
          console.error(`Error fetching stats for profile ${profile.id}:`, err)
          newStats[profile.id] = { totalAssessments: 0, averageScore: null }
        }
      }
      
      setProfileStats(newStats)
    }
    
    if (studentProfiles.length > 0) {
      fetchProfileStats()
    }
  }, [studentProfiles])

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    setFormLoading(true)

    try {
      if (!formName.trim()) {
        setFormError('Student name is required')
        setFormLoading(false)
        return
      }
      await createProfile(formName, formYearLevel)
      setFormName('')
      setFormYearLevel(5)
      setView('home')
    } catch (err: any) {
      setFormError(err.message || 'Failed to create profile')
    } finally {
      setFormLoading(false)
    }
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProfile) return
    
    setFormError(null)
    setFormLoading(true)

    try {
      if (!formName.trim()) {
        setFormError('Student name is required')
        setFormLoading(false)
        return
      }
      await updateProfile(editingProfile.id, formName, formYearLevel)
      setEditingProfile(null)
      setFormName('')
      setFormYearLevel(5)
      setView('home')
    } catch (err: any) {
      setFormError(err.message || 'Failed to update profile')
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditProfile = (profile: StudentProfile) => {
    setEditingProfile(profile)
    setFormName(profile.name)
    setFormYearLevel(profile.yearLevel)
    setView('edit-profile')
  }

  const handleDeleteProfile = async (profileId: string) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return
    
    setDeleteLoading(profileId)
    try {
      await deleteProfile(profileId)
    } catch (err: any) {
      alert(err.message || 'Failed to delete profile')
    } finally {
      setDeleteLoading(null)
    }
  }

  const handleSelectProfile = (profile: StudentProfile) => {
    // Check if user has credits before allowing to start writing
    if (!credits || credits.creditBalance <= 0) {
      alert('You need credits to submit a writing. Please purchase credits first.')
      return
    }
    setSelectedProfile(profile)
    setView('select-prompt')
  }

  const handleSelectPrompt = (prompt: WritingPrompt) => {
    setSelectedPrompt(prompt)
    setView('writing')
  }

  const handleBackToHome = () => {
    setSelectedProfile(null)
    setSelectedPrompt(null)
    setEditingProfile(null)
    setFormName('')
    setFormYearLevel(5)
    setFormError(null)
    setView('home')
  }

  const handleBackToPrompts = () => {
    setSelectedPrompt(null)
    setView('select-prompt')
  }

  const handleSubmissionSuccess = (submission: WritingSubmission) => {
    setLastSubmission(submission)
    setView('report')
  }

  const handleBackFromReport = () => {
    setLastSubmission(null)
    setSelectedPrompt(null)
    setView('select-prompt')
  }

  const handleViewProgress = (profile: StudentProfile) => {
    setSelectedProfile(profile)
    setView('progress')
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getUserInitial = () => {
    if (user?.name) return user.name[0].toUpperCase()
    if (user?.email) return user.email[0].toUpperCase()
    return 'U'
  }


  const renderCreateProfileModal = () => (
    <div className="modal-overlay" onClick={() => setView('home')}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Student Profile</h2>
          <button className="btn-close-modal" onClick={() => setView('home')}>×</button>
        </div>
        
        {formError && <div className="error-message">{formError}</div>}
        
        <form onSubmit={handleCreateProfile}>
          <div className="form-group-modern">
            <label htmlFor="name">Student Name</label>
            <input
              id="name"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter student's name"
              disabled={formLoading}
              required
            />
          </div>
          
          <div className="form-group-modern">
            <label htmlFor="yearLevel">Year Level</label>
            <select
              id="yearLevel"
              value={formYearLevel}
              onChange={(e) => setFormYearLevel(Number(e.target.value))}
              disabled={formLoading}
            >
              {[3, 4, 5, 6, 7, 8].map((level) => (
                <option key={level} value={level}>Year {level}</option>
              ))}
            </select>
          </div>
          
          <div className="form-actions-modern">
            <button type="button" className="btn-cancel" onClick={() => setView('home')} disabled={formLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={formLoading}>
              {formLoading ? 'Creating...' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  const renderEditProfileModal = () => (
    <div className="modal-overlay" onClick={handleBackToHome}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Student Profile</h2>
          <button className="btn-close-modal" onClick={handleBackToHome}>×</button>
        </div>
        
        {formError && <div className="error-message">{formError}</div>}
        
        <form onSubmit={handleUpdateProfile}>
          <div className="form-group-modern">
            <label htmlFor="name">Student Name</label>
            <input
              id="name"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter student's name"
              disabled={formLoading}
              required
            />
          </div>
          
          <div className="form-group-modern">
            <label htmlFor="yearLevel">Year Level</label>
            <select
              id="yearLevel"
              value={formYearLevel}
              onChange={(e) => setFormYearLevel(Number(e.target.value))}
              disabled={formLoading}
            >
              {[3, 4, 5, 6, 7, 8].map((level) => (
                <option key={level} value={level}>Year {level}</option>
              ))}
            </select>
          </div>
          
          <div className="form-actions-modern">
            <button type="button" className="btn-cancel" onClick={handleBackToHome} disabled={formLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={formLoading}>
              {formLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  const renderHomeContent = () => (
    <>
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-text">
          <h2>Welcome back, {user?.name || 'Parent'}! 👋</h2>
          <p>Track your children's writing progress and help them prepare for scholarship exams.</p>
        </div>
        <div className="welcome-stats">
          <div className="welcome-stat">
            <span className="welcome-stat-value">{studentProfiles.length}</span>
            <span className="welcome-stat-label">{studentProfiles.length === 1 ? 'Student' : 'Students'}</span>
          </div>
        </div>
      </div>

      {/* Credit Balance Section */}
      <div className="credit-section">
        <CreditBalance showBuyButton={true} />
        {credits && credits.creditBalance === 0 && (
          <div className="no-credits-warning">
            <p>⚠️ You need credits to submit writings for assessment.</p>
            <Link to="/pricing" className="btn-buy-credits">Buy Credits</Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <div className="quick-action-card" onClick={() => setView('create-profile')}>
          <div className="quick-action-icon purple">➕</div>
          <div className="quick-action-text">
            <h3>Add Student</h3>
            <p>Create a new student profile</p>
          </div>
        </div>
        <div className="quick-action-card" onClick={() => setView('examples')}>
          <div className="quick-action-icon green">📚</div>
          <div className="quick-action-text">
            <h3>Example Writings</h3>
            <p>View sample essays</p>
          </div>
        </div>
        <div className="quick-action-card" onClick={() => studentProfiles[0] && handleSelectProfile(studentProfiles[0])}>
          <div className="quick-action-icon orange">✍️</div>
          <div className="quick-action-text">
            <h3>Start Writing</h3>
            <p>Begin a practice session</p>
          </div>
        </div>
        <div className="quick-action-card" onClick={() => studentProfiles[0] && handleViewProgress(studentProfiles[0])}>
          <div className="quick-action-icon blue">📊</div>
          <div className="quick-action-text">
            <h3>View Progress</h3>
            <p>Check improvement trends</p>
          </div>
        </div>
      </div>

      {/* Student Profiles Section */}
      <div className="profiles-section">
        <div className="section-header">
          <h2>Student Profiles</h2>
          <button className="btn-add-profile" onClick={() => setView('create-profile')}>
            <span>+</span> Add Student
          </button>
        </div>

        {loading ? (
          <div className="loading-state-modern">
            <div className="loading-spinner"></div>
            <p>Loading profiles...</p>
          </div>
        ) : studentProfiles.length > 0 ? (
          <div className="profiles-grid-modern">
            {studentProfiles.map((profile) => (
              <div key={profile.id} className="profile-card-modern">
                <div className="profile-card-header">
                  <div className="profile-avatar">{getInitials(profile.name)}</div>
                  <div className="profile-info">
                    <h3>{profile.name}</h3>
                    <span className="profile-year-badge">Year {profile.yearLevel}</span>
                  </div>
                </div>
                
                <div className="profile-stats">
                  <div className="profile-stat">
                    <span className="profile-stat-value">
                      {profileStats[profile.id]?.totalAssessments ?? '-'}
                    </span>
                    <span className="profile-stat-label">Submissions</span>
                  </div>
                  <div className="profile-stat">
                    <span className="profile-stat-value">
                      {profileStats[profile.id]?.averageScore != null 
                        ? Number(profileStats[profile.id]?.averageScore).toFixed(1) 
                        : '-'}
                    </span>
                    <span className="profile-stat-label">Avg Score</span>
                  </div>
                </div>
                
                <div className="profile-actions-modern">
                  <button 
                    className="btn-profile-action primary"
                    onClick={() => handleSelectProfile(profile)}
                    disabled={deleteLoading === profile.id}
                  >
                    ✍️ Start Writing
                  </button>
                  <button 
                    className="btn-profile-action secondary"
                    onClick={() => handleViewProgress(profile)}
                    disabled={deleteLoading === profile.id}
                  >
                    📊 Progress
                  </button>
                  <button 
                    className="btn-profile-action secondary"
                    onClick={() => handleEditProfile(profile)}
                    disabled={deleteLoading === profile.id}
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="btn-profile-action danger"
                    onClick={() => handleDeleteProfile(profile.id)}
                    disabled={deleteLoading !== null}
                  >
                    {deleteLoading === profile.id ? '...' : '🗑️ Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state-modern">
            <div className="empty-state-icon">👨‍👩‍👧‍👦</div>
            <h3>No Student Profiles Yet</h3>
            <p>Create your first student profile to start practicing writing.</p>
            <button className="btn-add-profile" onClick={() => setView('create-profile')}>
              <span>+</span> Create First Profile
            </button>
          </div>
        )}
      </div>
    </>
  )


  const renderContent = () => {
    switch (view) {
      case 'create-profile':
        return (
          <>
            {renderHomeContent()}
            {renderCreateProfileModal()}
          </>
        )

      case 'edit-profile':
        return (
          <>
            {renderHomeContent()}
            {renderEditProfileModal()}
          </>
        )

      case 'select-prompt':
        return selectedProfile ? (
          <div className="dashboard-content-area">
            <div className="sub-view-header">
              <button onClick={handleBackToHome} className="btn-back">
                ← Back
              </button>
              <div className="sub-view-title">
                <h2>Select a Writing Prompt</h2>
                <p>Choose a prompt for {selectedProfile.name} (Year {selectedProfile.yearLevel})</p>
              </div>
            </div>
            <PromptSelector
              yearLevel={selectedProfile.yearLevel}
              onSelectPrompt={handleSelectPrompt}
              onBack={handleBackToHome}
            />
          </div>
        ) : null

      case 'writing':
        return selectedProfile && selectedPrompt ? (
          <div className="dashboard-content-area">
            <div className="sub-view-header">
              <button onClick={handleBackToPrompts} className="btn-back">
                ← Back to Prompts
              </button>
              <div className="sub-view-title">
                <h2>Writing Session</h2>
                <p>{selectedProfile.name} • Year {selectedProfile.yearLevel}</p>
              </div>
            </div>
            <div className="writing-view">
              <div className="prompt-display">
                <h3>{selectedPrompt.category.charAt(0).toUpperCase() + selectedPrompt.category.slice(1)} Writing</h3>
                <p className="prompt-text-display">{selectedPrompt.promptText}</p>
                {selectedPrompt.context && (
                  <p className="prompt-context">{selectedPrompt.context}</p>
                )}
                <p className="prompt-instructions-display">{selectedPrompt.instructions}</p>
              </div>
              <WritingInterface
                prompt={selectedPrompt}
                studentProfileId={selectedProfile.id}
                onSubmissionSuccess={handleSubmissionSuccess}
              />
            </div>
          </div>
        ) : null

      case 'report':
        return selectedProfile && lastSubmission?.assessmentId ? (
          <div className="dashboard-content-area">
            <AssessmentReport
              assessmentId={lastSubmission.assessmentId}
              studentProfileId={selectedProfile.id}
              yearLevel={selectedProfile.yearLevel}
              onBack={handleBackFromReport}
            />
          </div>
        ) : null

      case 'progress':
        return selectedProfile ? (
          <div className="dashboard-content-area">
            <div className="sub-view-header">
              <button onClick={handleBackToHome} className="btn-back">
                ← Back
              </button>
              <div className="sub-view-title">
                <h2>Progress Dashboard</h2>
                <p>Tracking {selectedProfile.name}'s writing improvement</p>
              </div>
            </div>
            <ProgressDashboard
              studentProfileId={selectedProfile.id}
              studentName={selectedProfile.name}
              yearLevel={selectedProfile.yearLevel}
            />
          </div>
        ) : null

      case 'examples':
        return (
          <div className="dashboard-content-area">
            <div className="sub-view-header">
              <button onClick={handleBackToHome} className="btn-back">
                ← Back
              </button>
              <div className="sub-view-title">
                <h2>Example Writings</h2>
                <p>Learn from sample essays at different quality levels</p>
              </div>
              <div className="year-selector">
                <label htmlFor="examplesYear">Year Level: </label>
                <select 
                  id="examplesYear"
                  value={examplesYearLevel} 
                  onChange={(e) => setExamplesYearLevel(Number(e.target.value))}
                  className="year-select"
                >
                  {[3, 4, 5, 6, 7, 8].map((level) => (
                    <option key={level} value={level}>Year {level}</option>
                  ))}
                </select>
              </div>
            </div>
            <ExampleWritings yearLevel={examplesYearLevel} />
          </div>
        )

      default:
        return (
          <div className="dashboard-content-area">
            {renderHomeContent()}
          </div>
        )
    }
  }

  return (
    <div className="modern-dashboard">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo">
          <span className="logo-icon">✍️</span>
          <span className="logo-text">Writing Assistant</span>
        </div>
        
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${view === 'home' ? 'active' : ''}`}
            onClick={handleBackToHome}
          >
            <span className="nav-icon">🏠</span>
            <span>Dashboard</span>
          </button>
          <button 
            className={`nav-item ${view === 'examples' ? 'active' : ''}`}
            onClick={() => setView('examples')}
          >
            <span className="nav-icon">📚</span>
            <span>Examples</span>
          </button>
          <button 
            className="nav-item"
            onClick={() => studentProfiles[0] && handleViewProgress(studentProfiles[0])}
          >
            <span className="nav-icon">📊</span>
            <span>Progress</span>
          </button>
          <a 
            href="/help"
            className="nav-item"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nav-icon">❓</span>
            <span>Help</span>
          </a>
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{getUserInitial()}</div>
            <div className="user-details">
              <div className="user-name">{user?.name || user?.email}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} className="btn-logout-sidebar">
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="dashboard-topbar">
          <div className="topbar-title">
            <h1>
              {view === 'home' && 'Dashboard'}
              {view === 'select-prompt' && 'Select Prompt'}
              {view === 'writing' && 'Writing Session'}
              {view === 'report' && 'Assessment Report'}
              {view === 'progress' && 'Progress'}
              {view === 'examples' && 'Example Writings'}
            </h1>
            <p>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        
        {renderContent()}
      </main>
    </div>
  )
}
